import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSaveLanguagePreference, useUserPreferences } from '../../services/complianceApi';

/**
 * The header language toggle (fleet i18n standard §3,
 * ptc-docs/coordination/plans/2026-10-21-fleet-i18n-standard.md).
 *
 * The choice persists twice: per BROWSER (i18next's detector caches it in localStorage, so the page
 * paints in it before sign-in) and per PERSON (the `language` preference, so it follows them to
 * another machine). A saved choice is applied ONCE, when the preferences arrive after sign-in; after
 * that the toggle owns it, and a choice made before they arrive is never overridden by them.
 *
 * Its own label is translated: it used to announce "Switch to Spanish" in English to a Spanish
 * screen-reader user.
 */
export default function LanguageToggle() {
  const { t, i18n } = useTranslation();
  const { data: prefs } = useUserPreferences();
  const { mutate: save } = useSaveLanguagePreference();
  const settled = useRef(false);

  const current = (i18n.resolvedLanguage ?? i18n.language ?? 'en').startsWith('es') ? 'es' : 'en';
  const next = current === 'es' ? 'en' : 'es';

  useEffect(() => {
    if (settled.current || !prefs) return;
    settled.current = true;
    const saved = prefs.language;
    if ((saved === 'en' || saved === 'es') && saved !== current) void i18n.changeLanguage(saved);
  }, [prefs, current, i18n]);

  return (
    <button
      type="button"
      className="lang-toggle"
      onClick={() => {
        settled.current = true;
        void i18n.changeLanguage(next);
        // A failed save leaves this browser's choice in place; the next toggle saves again.
        save(next);
      }}
      aria-label={t('common.language.switchTo')}
      title={t('common.language.switchTo')}
    >
      {current === 'es' ? t('common.language.es') : t('common.language.en')}
    </button>
  );
}
